import { useCallback, useEffect, useMemo, useState } from "react";
import { handlePromise } from "../../../utils/handlePromisse";
import { getAllTutorial } from "../../../services/tutorialService";
import { TutorialModel } from "../../../models/TutorialModel";

/** Remove diacríticos + lowercase — busca case/accent-insensitive */
function normalize(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export type CategoryOption = { value: string; label: string };

/** Estado e derivações da tela de Tutoriais (busca, categoria, destaque, grade). */
export function useTutorials() {
  const [allTutorial, setAllTutorial] = useState<TutorialModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<TutorialModel | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("todos");

  const fetchAllTutorial = useCallback(async () => {
    setLoading(true);
    const [result] = await handlePromise(getAllTutorial());
    if (result?.data) setAllTutorial(result.data as TutorialModel[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAllTutorial();
  }, [fetchAllTutorial]);

  const enabled = useMemo(() => allTutorial.filter((t) => t.enabled !== false), [allTutorial]);
  const featured = enabled[0] ?? null;

  // Categorias distintas — só quando ao menos um tutorial tem categoria
  const categoryOptions = useMemo<CategoryOption[] | null>(() => {
    const cats = Array.from(new Set(enabled.map((t) => t.category).filter((c): c is string => Boolean(c))));
    if (cats.length === 0) return null;
    return [{ value: "todos", label: "Todos" }, ...cats.map((c) => ({ value: c, label: c }))];
  }, [enabled]);

  const matchesFilters = useCallback(
    (list: TutorialModel[]) => {
      let result = list;
      if (selectedCategory !== "todos" && categoryOptions) {
        result = result.filter((t) => t.category === selectedCategory);
      }
      if (!search.trim()) return result;
      const q = normalize(search);
      return result.filter((t) => normalize(t.title).includes(q) || normalize(t.subtitle).includes(q));
    },
    [search, selectedCategory, categoryOptions]
  );

  // Sem busca: destaque fora da grade. Com busca: o destaque também é pesquisável.
  const filtered = useMemo(() => matchesFilters(enabled.slice(1)), [enabled, matchesFilters]);
  const allFiltered = useMemo(() => {
    const result = matchesFilters(enabled);
    return search.trim() ? result : result.slice(1);
  }, [enabled, matchesFilters, search]);

  const gridItems = search.trim() ? allFiltered : filtered;

  return {
    loading,
    search,
    setSearch,
    selectedCategory,
    setSelectedCategory,
    selectedVideo,
    setSelectedVideo,
    enabled,
    featured,
    categoryOptions,
    gridItems,
    allFiltered,
  };
}
